import type { IAgentTool } from '../IAgentTool';

const QUESTION_PREFIXES = [
  'what ',
  'who ',
  'which ',
  'when ',
  'where ',
  'why ',
  'how ',
  'can ',
  'should ',
  'would ',
  'is ',
  'are ',
  'do ',
  'does ',
];

function sanitizeChipText(input: unknown): string | null {
  const text = String(input ?? '')
    .replace(/\r?\n+/g, ' ')
    .replace(/[*_`~#>[\]()]/g, '')
    .replace(/^[-*•]\s+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.,;:]+$/g, '');

  if (!text) return null;
  const lower = text.toLowerCase();
  if (lower.includes('?')) return null;
  if (QUESTION_PREFIXES.some((prefix) => lower.startsWith(prefix))) return null;
  return text.slice(0, 40).trim();
}

export const suggestTool: IAgentTool = {
  name: 'suggest',
  description:
    'Present a question with clickable option buttons to the user. Call this when the user needs to choose between 2-4 options. The question provides context for the options — it is shown as the assistant message text.',
  parameters: {
    type: 'object',
    properties: {
      question: {
        type: 'string',
        description: 'The question or context text shown to the user above the option buttons. E.g. "Who should take over as VP Engineering?"',
      },
      options: {
        type: 'array',
        items: { type: 'string' },
        description: 'Array of 2-4 short option strings to present as clickable buttons',
      },
    },
    required: ['question', 'options'],
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'display',

  async execute(params) {
    const question = String(params.question ?? '').trim();
    const options = params.options as string[] | undefined;
    if (!question) {
      return { success: false, summary: 'No question provided.' };
    }
    if (!options || !Array.isArray(options) || options.length === 0) {
      return { success: false, summary: 'No options provided.' };
    }
    const cleaned = Array.from(
      new Set(options.map((option) => sanitizeChipText(option)).filter((option): option is string => Boolean(option))),
    ).slice(0, 4);
    if (cleaned.length === 0) {
      return { success: false, summary: 'No valid options provided.' };
    }
    return { success: true, summary: question, data: { question, suggestions: cleaned } };
  },
};
